import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Helmet } from "react-helmet";
import axios from 'axios';
import config from '../../config/config';
import RobotSelection from '../Common/RobotSelection';
import FolderSelection from '../Common/FolderSelection';
import ProcessSelection from '../Common/ProcessSelection';
import '../../styles/Triggers.css';

const EditWhatsAppTrigger = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [robot, setRobot] = useState('');
    const [folder, setFolder] = useState('');
    const [process, setProcess] = useState(null);

    const [priority, setPriority] = useState("Inherited");
    const [formErrors, setFormErrors] = useState({});
    const [stickToRobot, setStickToRobot] = useState(false);
    const priorities = [
        { value: "Inherited", label: "Inherited", icon: "○" },
        { value: "Critical", label: "Critical", icon: "◊" },
        { value: "Highest", label: "Highest", icon: "≫" },
        { value: "High", label: "High", icon: "∧" },
        { value: "Medium", label: "Medium", icon: "=" },
        { value: "Low", label: "Low", icon: "∨" },
        { value: "Lowest", label: "Lowest", icon: "≪" }
    ];

    const validateForm = () => {
        const errors = {};
        if (!name.trim()) errors.name = "Trigger name is required";
        if (!phoneNumber.trim()) {
            errors.phoneNumber = "WhatsApp number is required";
        } else if (!validatePhoneNumber(phoneNumber)) {
            errors.phoneNumber = "Invalid phone number format (e.g., +905551234567,+905320002233)";
        }
        if (!folder) errors.folder = "Folder selection is required";
        if (!process) errors.process = "Process selection is required";

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    useEffect(() => {
        const fetchTrigger = async () => {
            try {
                const token = localStorage.getItem('userToken');
                const response = await axios.get(
                    `${config.API_BASE_URL}/api/triggers/whatsapp/edit/${id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                const trigger = response.data;
                console.log('Trigger data received:', trigger);

                setName(trigger.name);
                setPhoneNumber(trigger.phone_number);
                setRobot(trigger.robot_id);
                setFolder(trigger.folder_id);

                setPriority(trigger.priority);
                setStickToRobot(Boolean(trigger.stickto_robot));

                if (trigger.process) {
                    setProcess({
                        id: trigger.process.id,
                        name: trigger.process.name,
                        process_key: trigger.process.process_key,
                        Key: trigger.process.process_key,  // Add this for ProcessSelection compatibility
                        ProcessKey: trigger.process.name    // Add this for ProcessSelection compatibility
                    });
                } else if (trigger.process_id) {
                    setProcess({
                        id: trigger.process_id,
                        name: trigger.process_name,
                        process_key: trigger.process_key,
                        Key: trigger.process_key,          // Add this for ProcessSelection compatibility
                        ProcessKey: trigger.process_name   // Add this for ProcessSelection compatibility
                    });
                } else {
                    setProcess(null);
                }
            } catch (error) {
                console.error('Error fetching trigger:', error);
                toast.error('Failed to load trigger details');
            }
        };

        fetchTrigger();
    }, [id]);

    // const validatePhoneNumber = (number) => {
    //     const phoneRegex = /^\+[1-9]\d{1,14}(,\+[1-9]\d{1,14})*$/;
    //     return phoneRegex.test(number);
    // };
    const validatePhoneNumber = (number) => {
        const phoneNumbers = number.split(',').map(num => num.trim());
        const phoneRegex = /^\+[1-9]\d{9,14}$/;
    
        const invalids = phoneNumbers.filter(num => !phoneRegex.test(num));
    
        if (invalids.length > 0) {
            toast.error(`Invalid phone number(s): ${invalids.join(', ')}`);
            return false;
        }
    
        return true;
    };
    

    const handleSubmit = async (e) => {
        e.preventDefault();

        // if (!validatePhoneNumber(phoneNumber)) {
        //     toast.error('Please enter a valid phone number with country code');
        //     return;
        // }

        if (!validateForm()) {
            toast.error('Please fill in all required fields');
            return;
        }


        try {
            const token = localStorage.getItem('userToken');
            const response = await axios.put(
                `${config.API_BASE_URL}/api/triggers/whatsapp/${id}`,
                {
                    name,
                    whatsappConfig: {
                        phone_number: phoneNumber,
                        message_filter: 'whatsapp',
                        process: process?.id || null,
                        robot_id: robot,
                        folder_id: folder,
                        priority,
                        stickto_robot: stickToRobot
                    },
                    folderId: folder
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            toast.success('WhatsApp trigger updated successfully');
            navigate('/dashboard');
        } catch (error) {
            console.error('Error updating WhatsApp trigger:', error);
            toast.error(error.response?.data?.error || 'Failed to update WhatsApp trigger');
        }
    };


    return (
        <div className="trigger-container">
            <Helmet><title>Edit WhatsApp Trigger - Autonomie</title></Helmet>
            <h2>Edit WhatsApp Trigger</h2>

            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>Trigger Name *</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className={formErrors.name ? 'error' : ''}
                        placeholder="Enter trigger name"
                        maxLength={255}
                    />
                    {formErrors.name && <span className="error-text">{formErrors.name}</span>}
                </div>

                {/* Updated Phone number field */}
                <div className="form-group">
                    <label>WhatsApp Number *</label>
                    <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className={formErrors.phoneNumber ? 'error' : ''}
                        placeholder="e.g., +905551234567,+905320002233"
                        pattern="^\+[1-9]\d{1,14}(,\+[1-9]\d{1,14})*$"
                    />
                    {formErrors.phoneNumber && <span className="error-text">{formErrors.phoneNumber}</span>}
                </div>
                <small className="hint-text">
                    Use international format: +905551234567,+905320002233 (no spaces)
                </small>
                    <div className="form-group">
                        <label>Priority</label>
                        <select
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                        >
                            {priorities.map((p) => (
                                <option key={p.value} value={p.value}>
                                    {p.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <RobotSelection
                            robot={robot}
                            onChange={setRobot}
                            folder={folder}
                        />
                    </div>

                    <FolderSelection
                        folder={folder}
                        onChange={setFolder}
                    />

                    <ProcessSelection
                        process={process}
                        onChange={(selectedProcess) => {
                            setProcess(selectedProcess ? {
                                id: selectedProcess.id,
                                name: selectedProcess.ProcessKey || selectedProcess.name,
                                process_key: selectedProcess.Key || selectedProcess.process_key,
                                Key: selectedProcess.Key || selectedProcess.process_key,
                                ProcessKey: selectedProcess.ProcessKey || selectedProcess.name
                            } : null);
                        }}
                        folder={folder}
                        error={formErrors.process}
                    />


                    <div className="form-group process-robot-lock">
                        <div className="robot-lock-container">
                            <input
                                type="checkbox"
                                id="stickToRobot"
                                checked={stickToRobot}
                                onChange={(e) => setStickToRobot(e.target.checked)}
                                disabled={robot === 'All'}
                            />
                            <label htmlFor="stickToRobot" className="robot-lock-label">
                                <span className="lock-icon">🔒</span>
                                Lock process to selected robot
                            </label>
                        </div>
                        {robot === 'All' ? (
                            <small className="help-text warning">
                                Select a specific robot to enable process locking
                            </small>
                        ) : (
                            <small className="help-text">
                                The process will only be executed by the selected robot
                            </small>
                        )}
                    </div>

                    <div className="button-group">
                        <button type="submit" className="submit-button">Update Trigger</button>
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={() => navigate('/dashboard')}
                        >
                            Cancel
                        </button>
                    </div>
               
            </form>
        </div>

    );
};

export default EditWhatsAppTrigger;